import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface AstroOgFaq {
  question: string;
  answer: string;
}

interface AstroOgImageProps {
  page_title?: string;
  faqs?: AstroOgFaq[];
}

interface ApproachRow {
  approach: string;
  build: string;
  freshness: string;
  ssr: string;
  fonts: string;
  caching: string;
  cost: string;
}

const ROWS: ApproachRow[] = [
  {
    approach: "Static file in public/",
    build:
      "Astro copies public/ into the build untouched. The docs describe these files as unprocessed. There is no image render step for that file.",
    freshness: "The bytes change when you replace the file and deploy. A CMS edit does not update the file.",
    ssr: "No adapter. The default static output is enough.",
    fonts: "Whatever you exported from the design tool. Astro does not read or subset the file.",
    caching:
      "The host serves the copied file. Social platforms cache the URL they fetched. This page does not publish their TTL. Change the file or the URL, then recheck the preview.",
    cost: "No image API. You maintain the artwork. One shared card is a poor fit when every post needs its own title.",
  },
  {
    approach: "Build-time endpoint (Satori or astro-og-canvas)",
    build:
      "On a static site, Astro calls the endpoint at build time and writes a file. astro-og-canvas OGImageRoute exports getStaticPaths and GET, one image per key in pages. More entries means more images during astro build. The package caches renders in ./node_modules/.astro-og-canvas by default. Set cacheDir to false to disable that. No duration is published here.",
    freshness:
      "The image matches the pages object at build: getCollection(), a markdown glob, or any object you pass. A remote CMS edit shows up after a rebuild that includes that entry. prerender = false runs the endpoint on request instead, and that requires an adapter.",
    ssr: "A prerendered dynamic route uses getStaticPaths and does not need an adapter. An on-demand route skips getStaticPaths and needs an adapter. Official adapters include Node, Netlify, Vercel, and Cloudflare.",
    fonts:
      "Satori requires font data for any text. Formats are TTF, OTF, and WOFF. WOFF2 is not supported. Emoji and missing glyphs go through loadAdditionalAsset. For emoji, return an image data URL when the code is emoji. Satori returns SVG, so a PNG response rasterizes that SVG. astro-og-canvas takes font paths or URLs and a families stack so a later family covers a missing glyph. Its README does not document an emoji image callback. pnpm also installs canvaskit-wasm.",
    caching:
      "astro-og-canvas cacheDir is a build cache, not a social-crawler cache. The emitted file stays static until the next build.",
    cost: "You run the library. npm lists astro-og-canvas as MIT. This page does not quote a host's build-minute price. You maintain the endpoint and the font files.",
  },
  {
    approach: "On-demand API (OG Pilot)",
    build:
      "createImage in prerendered frontmatter runs during astro build and returns a CDN URL. The Astro process does not rasterize the card. createImage on an on-demand route runs when that route is requested, so the build does not call the API for that page.",
    freshness:
      "The card matches the signed claims: template, title, description, path, and the other documented fields. A URL baked into static HTML stays there until the next build. An on-demand page can sign again when CMS data changes. If iat is set, the image cache is valid for 24 hours from that timestamp. If iat is omitted, the cache does not expire on its own. A newer iat can serve the previous image with stale: true while a new render is queued.",
    ssr: "No adapter when the call runs at build time. An adapter is required when the layout must run on each request. Keep the API key off the client: Astro exposes only PUBLIC_ variables to client code. Do not prefix OG_PILOT_API_KEY with PUBLIC_.",
    fonts:
      "The documented JWT claims do not include a font file or an emoji parameter. You pass title, description, colors, logo_url, and image_url. Named templates render the card. image_type may be jpeg, png, webp, or gif. The JSON response reports width 1200 and height 630.",
    caching:
      "Pass { iat: Date.now() } in the JavaScript SDK. The SDK converts milliseconds to seconds. Omit iat for a cache that does not expire on its own. Social crawlers still cache the URL they fetched. Recheck the public URL after the artwork changes.",
    cost: "7-day trial, no card, 100 completed images in this app. The API accepts a trial after DNS TXT verification. A trial is not enough for MCP generate_og_image. API: 600 requests per minute and 60,000 per hour per key. MCP generation: 10 per hour. The plan catalog lists Starter at $5/month ($50/year, 10,000-image quota), Builder at $49/month ($99/year, 100,000-image quota), and Growth at $99/month ($199/year, unlimited image quota). A Stripe image_quota feature can replace the quota. The catalog has no per-image price.",
  },
];

const COLUMNS: { key: keyof Omit<ApproachRow, "approach">; label: string }[] = [
  { key: "build", label: "Build time impact" },
  { key: "freshness", label: "Freshness for dynamic / CMS content" },
  { key: "ssr", label: "SSR / adapter" },
  { key: "fonts", label: "Fonts / emoji" },
  { key: "caching", label: "Caching" },
  { key: "cost", label: "Cost / maintenance" },
];

const BASE_HEAD_SAMPLE = `npm install og-pilot-js@^0.4.2

---
// src/components/BaseHead.astro
import { configure, createImage } from "og-pilot-js"

interface Props {
  title: string
  description: string
}

const { title, description } = Astro.props

configure((config) => {
  config.apiKey = import.meta.env.OG_PILOT_API_KEY
  config.domain = import.meta.env.OG_PILOT_DOMAIN
})

const ogImage = await createImage(
  {
    template: "page",
    title,
    description,
    path: Astro.url.pathname,
  },
  { iat: Date.now() },
)
---
<meta property="og:title" content={title} />
<meta property="og:description" content={description} />
<meta property="og:url" content={Astro.url.href} />
<meta property="og:image" content={ogImage} />
<meta property="og:image:width" content="1200" />
<meta property="og:image:height" content="630" />
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:image" content={ogImage} />`;

const HTTP_SAMPLE = `POST https://ogpilot.com/api/v1/images
Content-Type: application/json
Accept: application/json

{"token":"YOUR_JWT_TOKEN"}

Required JWT claims: iss (verified domain), sub (API key prefix).
Optional: iat, template, title, description, path, logo_url, image_url,
bg_color, text_color, accent_color, image_type, quality, max_bytes.
image_type: jpeg, png, webp, or gif.
The JavaScript SDK signs this token. Prefer the SDK over a hand-rolled JWT.
Docs: https://ogpilot.com/docs`;

const MCP_SAMPLE = `{
  "mcpServers": {
    "og-pilot": {
      "type": "streamable-http",
      "url": "https://ogpilot.com/mcp"
    }
  }
}

# Auth: OAuth 2.1 with PKCE. No API key in this file.
# Docs: https://ogpilot.com/docs/mcp

generate_og_image
{ "url": "https://example.com/blog/launch" }`;

const sectionClass =
  "mt-16 rounded-3xl border border-orange-200/80 bg-white/90 p-6 shadow-xl sm:p-8 dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function AstroOgImage({
  page_title: pageTitle = "Astro OG Image: Build vs API",
  faqs = [],
}: AstroOgImageProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                astro og image
              </Badge>
              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl dark:text-slate-100">
                Astro OG image: static, build-time, or on-demand API
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                An <strong>Astro OG image</strong> is the preview file a crawler reads from <code>og:image</code>. This
                page compares three ways to ship an Astro open graph image: a static file in <code>public/</code>, a
                build-time endpoint in the Satori or astro-og-canvas style, and a dynamic OG image from a hosted API.
                Figures below come from the Astro docs, the astro-og-canvas README, the Satori README, and this app.
                Where a number is missing, the cell says so.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button asChild size="lg" className="bg-orange-500 text-white hover:bg-orange-600">
                  <Link href="/sign_up">
                    Get an API key
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <div className="grid grid-cols-2 gap-3 sm:flex">
                  <Button asChild size="lg" variant="outline">
                    <Link href="/docs/mcp">Install MCP</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/docs">API docs</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/astro-og-image-generator">Astro setup</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section id="comparison" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Static, build-time, and on-demand</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Use the table when you are choosing how a dynamic OG image in Astro gets its bytes. A prerendered endpoint
              and a hosted API called from prerendered frontmatter both run during <code>astro build</code>. They differ
              in who draws the card, and in what you maintain afterward. Request-time rendering is a separate switch:{" "}
              <code>prerender = false</code> plus an adapter.
            </p>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[88rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Approach</th>
                    {COLUMNS.map((column) => (
                      <th key={column.key} className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.approach} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.approach}</th>
                      {COLUMNS.map((column) => (
                        <td key={column.key} className="px-3 py-3">
                          {row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Checked 2026-10-09. Sources:{" "}
              <a
                href="https://docs.astro.build/en/basics/project-structure/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Astro project structure
              </a>
              ,{" "}
              <a
                href="https://docs.astro.build/en/guides/endpoints/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Astro endpoints
              </a>
              ,{" "}
              <a
                href="https://docs.astro.build/en/guides/on-demand-rendering/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Astro on-demand rendering
              </a>
              ,{" "}
              <a
                href="https://docs.astro.build/en/guides/environment-variables/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Astro environment variables
              </a>
              ,{" "}
              <a
                href="https://github.com/delucis/astro-og-canvas/blob/latest/packages/astro-og-canvas/README.md"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                astro-og-canvas README
              </a>
              ,{" "}
              <a
                href="https://github.com/vercel/satori"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Satori
              </a>
              , and the{" "}
              <Link href="/docs" className={linkClass}>
                OG Pilot API docs
              </Link>
              . Pixel floors for the 1200×630 card are on the{" "}
              <Link href="/og-image-size" className={linkClass}>
                og:image size cheat sheet
              </Link>
              .
            </p>
          </section>

          <section id="choose" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Which one to ship</h2>
            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              <article className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">public/</h3>
                <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  One designed card covers the site, and you will replace the file when the artwork changes. A blog
                  where every post title should appear on the card outgrows this quickly.
                </p>
              </article>
              <article className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Build-time endpoint</h3>
                <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  Every entry is known at build, from a content collection or markdown, and you want the PNG in the
                  build output. You own the font files. Satori is JSX-to-SVG. astro-og-canvas is a CanvasKit route
                  helper, and its README is the layout contract.
                </p>
              </article>
              <article className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Hosted API</h3>
                <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  The same templates should work from Astro, Rails, or WordPress, or an agent should call{" "}
                  <Link href="/docs/mcp" className={linkClass}>
                    MCP
                  </Link>
                  . The Astro build stores a CDN URL. It does not store a font pipeline.
                </p>
              </article>
            </div>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              A remote CMS that edits a title between deploys needs the HTML to be generated again. Rebuild the static
              site, or render that route on demand. Calling the API from prerendered frontmatter does not watch the CMS
              after the build finishes. The shorter copy-paste setup, without this comparison, stays on the{" "}
              <Link href="/astro-og-image-generator" className={linkClass}>
                Astro OG image generator
              </Link>{" "}
              page.
            </p>
          </section>

          <section id="api" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              BaseHead meta tags with the OG Pilot API
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Install <code>og-pilot-js@^0.4.2</code>, the JavaScript SDK version in the API docs.{" "}
              <code>configure</code> reads <code>OG_PILOT_API_KEY</code> and <code>OG_PILOT_DOMAIN</code>.{" "}
              <code>createImage</code> signs the JWT and returns the image URL. <code>template</code> defaults to{" "}
              <code>page</code>. Use <code>blog_post</code> on a post layout when you have an author and a date. Pass{" "}
              <code>iat</code> when you want the documented 24-hour cache window. The frontmatter runs on the server
              during the build, or on each request when the page sets <code>prerender = false</code>.
            </p>
            <CodeSample label="src/components/BaseHead.astro" code={BASE_HEAD_SAMPLE} />
            <CodeSample label="Underlying HTTP call" code={HTTP_SAMPLE} />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Put <code>BaseHead</code> in the layout <code>&lt;head&gt;</code>. The documented JSON response includes{" "}
              <code>width</code> 1200 and <code>height</code> 630. There is no width or height request field. After the
              tags are live, check the public URL in the{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                Open Graph debugger
              </Link>
              . The same API from Next.js is on the{" "}
              <Link href="/nextjs-og-image-generator" className={linkClass}>
                Next.js OG image guide
              </Link>
              , and the longer Satori comparison is on the{" "}
              <Link href="/og-image-api" className={linkClass}>
                OG image API page
              </Link>
              .
            </p>
          </section>

          <section id="mcp" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">MCP install for an agent</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              The remote server is <code>https://ogpilot.com/mcp</code>. Transport is Streamable HTTP. Auth is OAuth 2.1
              with PKCE. The config file does not contain an API key. <code>generate_og_image</code> takes a public{" "}
              <code>url</code> and an optional <code>style</code>, and it needs an active plan and one credit. A trial
              can call the API after the domain is verified. It cannot call <code>generate_og_image</code>.{" "}
              <code>debug_open_graph</code>, <code>compress_image</code>, and <code>extract_color_palette</code> work on
              a guest connector session.
            </p>
            <CodeSample label="Remote MCP config" code={MCP_SAMPLE} />
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Claude and ChatGPT do not read this file. Follow the connector steps on{" "}
              <Link href="/docs/mcp" className={linkClass}>
                /docs/mcp
              </Link>
              . Full field lists stay on{" "}
              <Link href="/docs" className={linkClass}>
                /docs
              </Link>
              .
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">How to choose an Astro OG image</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>Use a file in public/ when one designed card covers the site.</li>
              <li>
                Prerender an endpoint with getStaticPaths when each content entry needs its own image at build time.
              </li>
              <li>
                Call the OG Pilot API from server-side layout frontmatter when the card should come from a hosted
                template.
              </li>
              <li>
                Render that route on demand, with an adapter, when CMS edits must change og:image without a full-site
                rebuild.
              </li>
              <li>Install the MCP server when an agent should call generate_og_image with the page URL.</li>
            </ol>
          </section>

          <ActivationGuide
            title="Get an API key or install the MCP server"
            links={[
              {
                href: "/sign_up",
                label: "Get an API key",
                detail: "Create an account, verify the domain with a DNS TXT record, and issue a signing key.",
              },
              {
                href: "/docs/mcp",
                label: "Install MCP",
                detail: "Connect https://ogpilot.com/mcp so an agent can call generate_og_image.",
              },
              {
                href: "/docs",
                label: "API docs",
                detail: "JWT claims, templates, the iat cache rule, and og-pilot-js.",
              },
              {
                href: "/astro-og-image-generator",
                label: "Astro setup page",
                detail: "The shorter layout sample, without the three-way comparison.",
              },
              {
                href: "/og-image-size",
                label: "og:image size",
                detail: "1200×630 and the platform floors this card is aimed at.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Confirm the tags on the public URL before a crawler caches them.",
              },
            ]}
          >
            <p>
              Pick <code>public/</code> or a prerendered endpoint when the card lives entirely in the Astro repo. Get an
              API key when the preview should come from a hosted template, including from{" "}
              <Link href="/new" className={linkClass}>
                one-click generation
              </Link>{" "}
              while you are still wiring the layout. Install MCP when the next step is an agent with the page URL.
            </p>
          </ActivationGuide>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Astro OG image questions</h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className="mt-20 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-10 text-white shadow-xl">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <h2 className="text-3xl font-bold sm:text-4xl">Ship the Astro card from an API key or MCP</h2>
                <p className="mt-4 max-w-xl text-sm leading-6 text-slate-200">
                  Keep a static file or a build-time endpoint when you want the PNG in the repo output. Use OG Pilot
                  when the layout should store a CDN URL and an agent should be able to generate the same card.
                </p>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/10 p-6">
                <div className="flex flex-col gap-3">
                  <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                    <Link href="/sign_up">
                      Get an API key
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/docs/mcp">Install MCP</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/docs">Docs</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
